import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Trash2, ExternalLink, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, fmt } from "@/lib/api";
import { usePanel } from "./PanelContext";
import { PaymentPicker } from "./NewOrderDialog";

const menuUrl = (slug, tableId) => `${window.location.origin}/menu/${slug}${tableId ? `?masa=${tableId}` : ""}`;

function downloadQr(id, name) {
  const canvas = document.getElementById(id);
  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/png");
  a.download = `${name}-qr.png`;
  a.click();
}

export default function Tables() {
  const { api, biz, activeOrders } = usePanel();
  const [tables, setTables] = useState([]);
  const [name, setName] = useState("");
  const [area, setArea] = useState("Salon");
  const [closing, setClosing] = useState(null);
  const [pay, setPay] = useState("nakit");

  const load = () => api.get("/tables").then((r) => setTables(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [activeOrders.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/tables", { name, area }); setName(""); load(); toast.success("Masa eklendi"); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (t) => { if (!window.confirm(`${t.name} silinsin mi?`)) return; await api.delete(`/tables/${t.id}`); load(); };
  const close = async () => {
    try { await api.post(`/tables/${closing.id}/close`, { payment_method: pay }); toast.success("Hesap kapatıldı"); setClosing(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6 rise" data-testid="tables-page">
      <div><h1 className="text-3xl font-extrabold">Masalar & QR Kod</h1><p className="text-sm text-slate-500">Her masanın QR kodunu yazdırıp masaya koyun. Müşteriler menüyü görüp masadan sipariş verir.</p></div>
      <div className="rounded-3xl bg-white border p-5 flex flex-wrap gap-3 items-center">
        <Input placeholder="Masa adı (ör. Masa 1)" className="max-w-xs" value={name} onChange={(e) => setName(e.target.value)} data-testid="table-name-input" />
        <Input placeholder="Bölüm" className="max-w-[160px]" value={area} onChange={(e) => setArea(e.target.value)} data-testid="table-area-input" />
        <Button onClick={add} className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00] gap-1" data-testid="table-add-btn"><Plus className="h-4 w-4" />Masa Ekle</Button>
        <div className="ml-auto flex items-center gap-3 text-sm">
          <QRCodeCanvas id="qr-general" value={menuUrl(biz.slug)} size={56} />
          <div><p className="font-semibold">Paket Sipariş QR'ı</p>
            <p className="text-xs text-slate-500">Bunu okutan müşteri adresini girip eve sipariş verir</p>
            <a href={menuUrl(biz.slug)} target="_blank" rel="noreferrer" className="text-[#FF6B0A] text-xs flex items-center gap-1" data-testid="general-menu-link">Menüyü aç <ExternalLink className="h-3 w-3" /></a></div>
          <Button size="icon" variant="outline" onClick={() => downloadQr("qr-general", "menu")} data-testid="general-qr-download"><Download className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {tables.map((t) => (
          <div key={t.id} className={`rounded-3xl border p-5 bg-white ${t.open_count ? "border-[#FF6B0A]" : ""}`} data-testid={`table-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div><p className="font-display text-xl font-bold">{t.name}</p><p className="text-xs text-slate-500">{t.area}</p></div>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${t.open_count ? "bg-[#FF6B0A] text-white" : "bg-emerald-100 text-emerald-800"}`}>{t.open_count ? "Dolu" : "Boş"}</span>
            </div>
            <div className="mt-4 flex justify-center bg-slate-50 rounded-2xl p-4"><QRCodeCanvas id={`qr-${t.id}`} value={menuUrl(biz.slug, t.id)} size={128} /></div>
            {t.open_count > 0 && <p className="mt-3 text-sm">Açık hesap: <b data-testid={`table-open-total-${t.id}`}>{fmt(t.open_total)}</b> ({t.open_count} sipariş)</p>}
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full flex-1 gap-1" onClick={() => downloadQr(`qr-${t.id}`, t.name)} data-testid={`table-qr-download-${t.id}`}><Download className="h-3.5 w-3.5" />QR</Button>
              {t.open_count > 0 && <Button size="sm" className="rounded-full flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => setClosing(t)} data-testid={`table-close-btn-${t.id}`}>Hesabı Kapat</Button>}
              <Button size="icon" variant="ghost" className="text-red-600" onClick={() => del(t)} data-testid={`table-delete-btn-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      {!tables.length && <div className="rounded-3xl border border-dashed p-12 text-center text-slate-500">Henüz masa eklenmedi.</div>}
      <Dialog open={!!closing} onOpenChange={(o) => !o && setClosing(null)}>
        <DialogContent data-testid="close-table-dialog">
          <DialogHeader><DialogTitle>{closing?.name} • Hesabı Kapat</DialogTitle></DialogHeader>
          <p className="font-display text-3xl font-extrabold">{fmt(closing?.open_total)}</p>
          <PaymentPicker value={pay} onChange={setPay} prefix="close-table-payment" />
          <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={close} data-testid="close-table-confirm-btn">Ödemeyi Al ve Kapat</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
